import { useRouter } from 'next/router';
import { createContext, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { ACCESS_TOKEN_KEY, AVAILABLE_COMBO_STOCK, AVAILABLE_STOCK, CLIENT_NAME_COOKIE, COMMON, COOKIE_MAX_AGE_24HOURS, DIGIACRE_DOMAIN, INSURANCE_KEY, IS_CREDIT_USER, MIN_TOKEN_VALIDITY_MINUTE, MY_CART, OUT_OF_STOCK_COMBOS, OUT_OF_STOCK_PRODUCTS, REFRESH_TOKEN_KEY, SERVICE_CART, SERVICE_ID, SERVICE_VENDOR_ID, SHRI_PROGRAM_USER_DETAILS } from '@/constants/common';
import Mixpanel from '@/utils/analytics/mixpanel';
import { clearSuperPropertiesStore } from '@/utils/analytics/userProperties';
import { persistSessionUtmParams } from '@/utils/analytics/utmParams';
import keycloakLogout from '@/utils/api/logout';
import { AUTH_TOKENS_UPDATED_EVENT } from '@/utils/auth/handle401Refresh';
import refreshCoordinator, { setWebviewClosing } from '@/utils/auth/refreshCoordinator';
import { deleteCookie, getCookieValue, isCSCUser, isSessionStorageAvailable, persistToken, postMessageToWebView, removeLocalStorageKey } from '@/utils/helper';
import { destroyMoengageSession } from '@/utils/moengage';
import parseJWT from '@/utils/parseToken';
interface AuthContextProps {
  isAuthenticated: boolean;
  isAuthReady: boolean;
  persistTokens: VoidFunction;
  logout: (redirectUrl?: string) => void;
}
export const AuthContext = createContext<AuthContextProps>({
  isAuthenticated: false,
  isAuthReady: false,
  logout: () => undefined,
  persistTokens: () => undefined
});
interface Props {
  children: ReactNode;
}
const AuthProvider = ({
  children
}: Props) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const {
    asPath,
    reload,
    pathname,
    query
  } = useRouter();
  const logout = useCallback((redirectUrl?: string) => {
    if ('ReactNativeWebView' in window) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      postMessageToWebView(window, {
        code: 'ERR_001',
        message: 'logout'
      });
      return;
    }
    if (refreshToken) {
      keycloakLogout(refreshToken);
    }
    const parentDomain = isCSCUser(accessToken) ? DIGIACRE_DOMAIN : '';
    setAccessToken(null);
    setRefreshToken(null);
    persistToken(ACCESS_TOKEN_KEY, '', -1, parentDomain);
    persistToken(REFRESH_TOKEN_KEY, '', -1, parentDomain);
    clearLocalStorage();
    deleteCookie(CLIENT_NAME_COOKIE);
    deleteCookie('user_id');
    if (isSessionStorageAvailable(window)) {
      sessionStorage.removeItem(AVAILABLE_STOCK);
      sessionStorage.removeItem(AVAILABLE_COMBO_STOCK);
      sessionStorage.removeItem(OUT_OF_STOCK_PRODUCTS);
      sessionStorage.removeItem(OUT_OF_STOCK_COMBOS);
    }
    Mixpanel.reset();
    clearSuperPropertiesStore();
    destroyMoengageSession();
    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      reload();
    }
  }, [refreshToken, accessToken, reload]);
  const hasInitializedAuth = useRef(false);
  const persistTokens = useCallback(() => {
    // Only set isAuthReady to false on the very first call (initial mount).
    // On subsequent pathname changes (e.g., /app-entry → target page),
    // skip the false→true cycle to prevent loader flicker (behavior b).
    if (!hasInitializedAuth.current) {
      setIsAuthReady(false);
    }
    const accessToken = getCookieValue(ACCESS_TOKEN_KEY) || null;
    const refreshToken = getCookieValue(REFRESH_TOKEN_KEY) || null;
    setAccessToken(accessToken);
    setRefreshToken(refreshToken);
    setIsAuthReady(true);
    hasInitializedAuth.current = true;
  }, []);
  useEffect(() => {
    persistTokens();
  }, [persistTokens, pathname]);

  /**
   * Background token refresh loop.
   * Every TOKEN_CHECK_INTERVAL, check cookie-based access token expiry.
   * If remaining validity is below MIN_TOKEN_VALIDITY_MINUTE, refresh tokens
   * using refreshCoordinator, persist them to cookies, and notify listeners.
   * Uses refreshCoordinator's internal inflight promise to avoid parallel calls.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const intervalMs = (COMMON?.TOKEN_CHECK_INTERVAL ?? MIN_TOKEN_VALIDITY_MINUTE * 60 * 1000) || 60000;
    const minValiditySeconds = MIN_TOKEN_VALIDITY_MINUTE * 60;
    const computeRemainingSeconds = (token: string | null): number => {
      if (!token) return 0;
      try {
        const payload = parseJWT(token);
        if (typeof payload.exp === 'number') {
          const nowSec = Math.floor(Date.now() / 1000);
          return payload.exp - nowSec;
        }
      } catch {
        // ignore parse errors, treat as expired
      }
      return 0;
    };
    const checkAndRefresh = async () => {
      try {
        const accessFromCookie = getCookieValue(ACCESS_TOKEN_KEY) || null;
        const refreshFromCookie = getCookieValue(REFRESH_TOKEN_KEY) || null;
        if (!accessFromCookie || !refreshFromCookie) {
          return;
        }

        // Initial check
        let remaining = computeRemainingSeconds(accessFromCookie);
        if (remaining > minValiditySeconds) {
          return;
        }

        // Final check with latest cookies just before calling refresh
        const latestAccess = getCookieValue(ACCESS_TOKEN_KEY) || null;
        const latestRefresh = getCookieValue(REFRESH_TOKEN_KEY) || null;
        if (!latestAccess || !latestRefresh) {
          return;
        }
        remaining = computeRemainingSeconds(latestAccess);
        if (remaining > minValiditySeconds) {
          return;
        }
        const authToken = await refreshCoordinator.refresh(latestRefresh);
        persistToken(ACCESS_TOKEN_KEY, authToken.access_token, authToken.expires_in);
        persistToken(REFRESH_TOKEN_KEY, authToken.refresh_token, authToken.refresh_expires_in);
        window.dispatchEvent(new CustomEvent(AUTH_TOKENS_UPDATED_EVENT));
      } catch {
        // On failure, let existing 401 handlers / middleware handle logout or retries.
      }
    };
    const id = window.setInterval(checkAndRefresh, intervalMs);
    // Run once on mount so we don't wait a full interval when already near expiry.
    void checkAndRefresh();
    return () => {
      window.clearInterval(id);
    };
  }, []);

  /**
   * When the 401 interceptor (or BL kheti/locus/ondc) refreshes tokens client-side,
   * it dispatches auth-tokens-updated. Re-read cookies so React state stays in sync.
   */
  useEffect(() => {
    const handler = () => persistTokens();
    window.addEventListener(AUTH_TOKENS_UPDATED_EVENT, handler);
    return () => window.removeEventListener(AUTH_TOKENS_UPDATED_EVENT, handler);
  }, [persistTokens]);

  /**
   * Detect middleware-driven token refresh via the kc_token_sync flag cookie.
   * When the middleware refreshes tokens server-side for a WebView request,
   * it sets kc_token_sync=1 so the client can notify native on hydration.
   */
  useEffect(() => {
    if (typeof window === 'undefined' || !('ReactNativeWebView' in window)) {
      return;
    }
    const syncFlag = getCookieValue('kc_token_sync');
    if (!syncFlag) {
      return;
    }
    const currentAccessToken = getCookieValue(ACCESS_TOKEN_KEY);
    const currentRefreshToken = getCookieValue(REFRESH_TOKEN_KEY);
    if (currentAccessToken && currentRefreshToken) {
      let expiresIn = 0;
      const refreshExpiresIn = COOKIE_MAX_AGE_24HOURS;
      try {
        const payload = parseJWT(currentAccessToken);
        if (typeof payload.exp === 'number') {
          expiresIn = Math.max(0, payload.exp - Math.floor(Date.now() / 1000));
        }
      } catch {
        // use defaults
      }
      postMessageToWebView(window, {
        code: 'INFO_001',
        message: {
          access_token: currentAccessToken,
          refresh_token: currentRefreshToken,
          expires_in: expiresIn,
          refresh_expires_in: refreshExpiresIn
        }
      });
    }

    // Clear the sync flag to prevent duplicate notifications
    document.cookie = 'kc_token_sync=; max-age=-1; path=/; secure;';
  }, [pathname]);

  /**
   * Listen for WEBVIEW_CLOSING message from the React Native app.
   * When received, signal the refresh coordinator to stop all refresh activity.
   */
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }
      if (data?.code === 'WEBVIEW_CLOSING') {
        setWebviewClosing();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  /**
   * To be triggered everytime route changes to send
   * current page title to React Native App
   */

  useEffect(() => {
    if ('ReactNativeWebView' in window) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      postMessageToWebView(window, {
        code: 'TITLE',
        message: document.title
      });
    }
  }, [asPath]);

  // UTM params persist for the browser session (not cleared on logout).
  useEffect(() => {
    persistSessionUtmParams(query);
  }, [asPath, query]);
  return <AuthContext.Provider value={{
    isAuthenticated: !!accessToken && !!refreshToken,
    isAuthReady,
    logout,
    persistTokens
  }} data-sentry-element="unknown" data-sentry-component="AuthProvider" data-sentry-source-file="AuthProvider.tsx">
      {children}
    </AuthContext.Provider>;
};
function clearLocalStorage() {
  removeLocalStorageKey(MY_CART);
  removeLocalStorageKey(INSURANCE_KEY);
  removeLocalStorageKey(SHRI_PROGRAM_USER_DETAILS);
  removeLocalStorageKey(SERVICE_CART);
  removeLocalStorageKey(SERVICE_ID);
  removeLocalStorageKey(SERVICE_VENDOR_ID);
  removeLocalStorageKey(IS_CREDIT_USER);
}
export default AuthProvider;